import { Stack, type StackProps } from '@chakra-ui/react'
import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useCompact } from '~/theme/media'

/**
 * The bottom action area of the app shell (MemberLayout): the element ActionBar renders into on a phone. `null` until
 * the shell has mounted it; `undefined` outside a MemberLayout (the bar then stays where it stands).
 */
export const ActionBarSlot = createContext<HTMLElement | null | undefined>(undefined)

/**
 * The bar that holds the main action of a screen (design spec 2026-10-30 §2.1).
 *
 * On a phone (narrower than 768 px, or under 500 px tall) the shell keeps the action area under its scroller, always
 * in view and above the on-screen keyboard: ActionBar renders its children there, through a portal, so the button
 * never scrolls away and never covers a field. On tablets and computers it is a plain block where it stands (the last
 * block of the page card).
 *
 * A portal takes the button out of its `<form>`, so a submit button names its form: give the form an `id` and the
 * button `form={id}`. Buttons that are not submits, and links, need nothing.
 *
 *   const formId = useId()
 *   <FormStack id={formId} gap={5} onSubmit={submit} noValidate>
 *     ...fields
 *     <ActionBar>
 *       <Button type='submit' form={formId} size='lg' w='full'>{t('about.continue')}</Button>
 *     </ActionBar>
 *   </FormStack>
 */
export function ActionBar({ children, ...rest }: { children: ReactNode } & StackProps) {
  const slot = useContext(ActionBarSlot)
  const compact = useCompact()
  const bar = (
    <Stack data-action-bar='' gap={3} {...rest}>
      {children}
    </Stack>
  )
  if (!compact || slot === undefined) return bar
  // On a phone the bar lives in the shell's action area only (nothing in place, also during the shell's first commit).
  return slot ? createPortal(bar, slot) : null
}
